import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialoglogin";
import { useActionState, useEffect, useState } from "react";
import { DialogTitle } from "@radix-ui/react-dialog";
import { Input } from "@/components/ui/input";
import { api, backendApi } from "@/services/api.service";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@nextui-org/react";
import { Button } from "@/components/ui/button";
import { toast } from "react-toastify";
import {
  useGetTransactionDetail,
  useUpdateTransactionStatus,
} from "@/services/withdraw.service";
import LoadingAnimation from "@/components/games/spin-wheel/components/LoadingAnimation";
import { format, parse } from "date-fns";
import { CopyIcon } from "@/assets/icons/admin";
import { handleCopy } from "@/utils/common";
import { TransactionStatus } from "@/components/general/TransactionStatus";

interface TransactionDetailDialogProps {
  transactionId: string;
  open: boolean;
  onClose: () => void;
  onRefetch: () => void;
}

const data = [
  {
    title: "Transaction ID",
    key: "transactionId",
  },
  {
    title: "Date and Time",
    key: "createdAt",
  },
  {
    title: "Email",
    key: "email",
  },
  {
    title: "Username",
    key: "username",
  },
  {
    title: "Method",
    key: "method",
  },
  {
    title: "KYC Status",
    key: "kycStatus",
  },
  {
    title: "Referral Link",
    key: "referalCode",
  },
  {
    title: "IP Address",
    key: "lastIp",
  },
  {
    title: "Network",
    key: "chain",
  },
  {
    title: "To",
    key: "walletAddress",
  },
  {
    title: "Amount",
    key: "amount",
  },
  {
    title: "Type",
    key: "currency",
  },
  {
    title: "Value",
    key: "usdAmount",
  },
  {
    title: "Balance",
    key: "prevBalance",
  },
  {
    title: "New Balance",
    key: "newBalance",
  },
  {
    title: "Processed Date",
    key: "updatedAt",
  },
  {
    title: "Note",
    key: "note",
  },
  {
    title: "Promo Code",
    key: "promoCode",
  },
];

const status = [
  {
    label: "Approve",
    value: "approve",
  },
  {
    label: "Reject",
    value: "reject",
  },
];

const rejectionReasons = [
  "Wrong wallet address",
  "Suspicious action",
  "KYC unverified",
  "Wagering requirement not met",
  "Bonus funds abuse",
  "Non conforming gameplay",
  "Other reason",
];

export const TransactionDetailDialog = ({
  transactionId,
  open,
  onClose,
  onRefetch,
}: TransactionDetailDialogProps) => {
  const {
    data: response,
    isPending,
    isError,
  } = useGetTransactionDetail(transactionId);

  const { mutate: updateStatus, isPending: isPendingUpdate } =
    useUpdateTransactionStatus(transactionId);

  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedRejectReason, setSelectedRejectReason] = useState("");
  const [transactionHash, setTransactionHash] = useState("");

  const transaction = response?.data.data;

  const renderKey = (item: any, transaction: any) => {
    switch (item.key) {
      case "kycStatus":
        return (
          <p
            className={`text-sm ${transaction?.kycStatus === "completed" ? "text-success" : "text-danger"}`}
          >
            {transaction?.kycStatus === "completed" ? "Verified" : "Unverified"}
          </p>
        );

      case "walletAddress":
      case "referalCode":
        return (
          <div
            className={`flex items-center gap-2 text-wrap break-all text-sm`}
          >
            {transaction[item.key] ?? "-"}
            <div
              className="hover:cursor-pointer"
              onClick={() => handleCopy(transaction[item.key])}
            >
              <CopyIcon width={18} height={18} />
            </div>
          </div>
        );

      case "amount":
        return (
          <p className="text-sm">
            {(transaction[item.key] as number).toLocaleString(undefined, {
              maximumFractionDigits: 4,
            }) ?? "0"}
          </p>
        );

      case "usdAmount":
      case "prevBalance":
        return (
          <p className="text-sm">
            $
            {(transaction[item.key] as number).toLocaleString(undefined, {
              maximumFractionDigits: 4,
            }) ?? "0"}
          </p>
        );

      case "promoCode":
        return (
          <p className="text-sm">
            {transaction[item.key] === "" ? "-" : transaction[item.key]}
          </p>
        );

      case "createdAt":
      case "updatedAt":
        return (
          <p className="text-sm">
            {transaction?.createdAt
              ? format(transaction.createdAt, "dd/MM/yyyy HH:mm:ss")
              : "-"}
          </p>
        );

      default:
        return <p className="text-sm">{transaction[item.key] ?? "-"}</p>;
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        onClose={onClose}
        className={`alert-bg max-h-[900px] overflow-auto bg-[linear-gradient(#021556,#020522)] bg-cover md:max-h-[95%]`}
      >
        <DialogTitle className="flex items-center gap-6">
          <span className="text-2xl text-white">{`Transaction Details`}</span>
          <div className="text-sm">
            {TransactionStatus(
              transaction?.isCompleted,
              transaction?.isRejected,
              transaction?.isCanceled,
            )}
          </div>
        </DialogTitle>
        {isPending ? (
          <LoadingAnimation />
        ) : isError ? (
          <div className="flex h-full w-full items-center justify-center text-white ">
            Transaction detail not available
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-2 font-kanit font-[200] text-white ">
              {data.map((d) => {
                if (
                  !transaction.isCompleted &&
                  (d.key === "updatedAt" || d.key === "note")
                ) {
                  return;
                }
                return (
                  <div>
                    <span className="text-xs text-white/50">{d.title}</span>
                    {renderKey(d, transaction)}
                  </div>
                );
              })}

              {!transaction?.isCompleted &&
                (transaction?.transactionType === "WITHDRAW" ||
                  transaction?.transactionType === "DEPOSIT") && (
                  <div>
                    <span className="text-xs text-white/50">Approval</span>
                    <Select
                      value={selectedStatus}
                      onValueChange={(e) => {
                        if (e === "approve") {
                          setSelectedRejectReason("");
                        }
                        setSelectedStatus(e);
                      }}
                    >
                      <SelectTrigger className="w-full border-2 border-white/5 bg-white/5 text-start text-sm text-white">
                        <SelectValue placeholder="Select approval status" />
                      </SelectTrigger>
                      <SelectContent className="border-2 border-white/5 bg-[#14142A]">
                        {status.map((item) => (
                          <SelectItem
                            key={item.value}
                            value={item.value}
                            className="w-full"
                          >
                            <div className="flex w-full items-center justify-between gap-2">
                              <div className="flex items-center">
                                <div className="text-sm text-white">
                                  {item.label}
                                </div>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

              {!transaction.isCompleted && selectedStatus === "reject" && (
                <div>
                  <span className="text-xs text-white/50">
                    Rejection Reason
                  </span>
                  <Select
                    value={selectedRejectReason}
                    onValueChange={(e) => setSelectedRejectReason(e)}
                  >
                    <SelectTrigger className="w-full border-2 border-white/5 bg-white/5 text-start text-sm text-white">
                      <SelectValue placeholder="Select rejection status" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-white/5 bg-[#14142A]">
                      {rejectionReasons.map((item) => (
                        <SelectItem key={item} value={item} className="w-full">
                          <div className="flex w-full items-center justify-between gap-2">
                            <div className="flex items-center">
                              <div className="text-sm text-white">{item}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {!transaction.isCompleted && selectedStatus === "approve" && (
                <div>
                  <span className="text-xs text-white/50">
                    Transaction Hash
                  </span>
                  <Input
                    required
                    readOnly={transaction.isCompleted}
                    value={transactionHash}
                    onChange={(e) => setTransactionHash(e.target.value)}
                  />
                </div>
              )}

              {transaction.isCompleted && (
                <div>
                  <span className="text-xs text-white/50">
                    Transaction Hash
                  </span>
                  <div
                    className={`flex items-center gap-2 text-wrap break-all text-sm`}
                  >
                    {transaction?.txHash ?? "-"}
                    <div
                      className="hover:cursor-pointer"
                      onClick={() => handleCopy(transaction?.txHash)}
                    >
                      <CopyIcon width={18} height={18} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!transaction?.isCompleted &&
              (transaction?.transactionType === "WITHDRAW" ||
                transaction?.transactionType === "DEPOSIT") && (
                <Button
                  disabled={isPendingUpdate}
                  className={`flex w-32 self-end rounded-sm bg-button-gradient ${isPendingUpdate && "bg-primary text-white/40"}`}
                  onClick={() => {
                    updateStatus(
                      {
                        status: selectedStatus,
                        note: selectedRejectReason,
                        txHash: transactionHash,
                      },
                      {
                        onError: (err: any) => {
                          console.log(err);
                          toast.error(err.message);
                        },
                        onSuccess: () => {
                          toast.success("Transaction successfully updated");
                          onClose();
                          onRefetch();
                        },
                      },
                    );
                  }}
                >
                  Save
                </Button>
              )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
